import { useRef, useState } from 'react'
import { apiUploadFoto } from '../lib/api'
import { useHubContext } from './HubLayout'
import { ImageCropModal } from './ImageCropModal'

type Props = {
  className?: string
}

export function FotoPerfilCard({ className }: Props) {
  const { hub, setHub, error, setError } = useHubContext()
  const [status, setStatus] = useState('')
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const cropObjectUrl = useRef<string | null>(null)

  if (!hub) return null

  function clearCropSrc() {
    if (cropObjectUrl.current) {
      URL.revokeObjectURL(cropObjectUrl.current)
      cropObjectUrl.current = null
    }
    setCropSrc(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  function onFileChosen(file: File | null) {
    if (!file) return
    if (file.size > 20 * 1024 * 1024) {
      setError('Arquivo muito grande. Máximo: 20MB.')
      return
    }
    setError('')
    setStatus('')
    clearCropSrc()
    const url = URL.createObjectURL(file)
    cropObjectUrl.current = url
    setCropSrc(url)
  }

  async function onCropConfirm(file: File) {
    setStatus('Enviando…')
    setError('')
    // Preview imediato (mesmo padrão do Eu Apoio com blob local).
    const previewUrl = URL.createObjectURL(file)
    setHub((prev) => (prev ? { ...prev, foto: { tem_foto: true, url: previewUrl } } : prev))
    try {
      const next = await apiUploadFoto(file)
      setHub(next)
      setStatus('Foto atualizada!')
      clearCropSrc()
      URL.revokeObjectURL(previewUrl)
    } catch (err) {
      URL.revokeObjectURL(previewUrl)
      setError(err instanceof Error ? err.message : 'Falha no upload')
      setStatus('')
      throw err
    }
  }

  return (
    <section className={['bp-card', className].filter(Boolean).join(' ')}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <button
          type="button"
          className="bp-avatar"
          onClick={() => fileRef.current?.click()}
          title="Trocar foto"
          style={{ border: 0, padding: 0 }}
        >
          {hub.foto.tem_foto && hub.foto.url ? (
            <img src={hub.foto.url} alt="Sua foto" />
          ) : (
            (hub.user.nome || 'L').slice(0, 1).toUpperCase()
          )}
        </button>
        <div style={{ flex: 1 }}>
          <strong>{hub.foto.tem_foto ? 'Sua foto de perfil' : 'Adicione sua foto'}</strong>
          <p className="bp-muted" style={{ margin: '0.25rem 0 0.5rem' }}>
            Aparece na árvore e no ranking. Você pode enquadrar e dar zoom antes de salvar.
          </p>
          <button type="button" className="bp-btn bp-btn--primary" onClick={() => fileRef.current?.click()}>
            <span className="bp-btn__icon" aria-hidden>
              📷
            </span>
            {hub.foto.tem_foto ? 'Trocar foto' : 'Escolher foto'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,.heic,.heif"
            hidden
            onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
          />
          {status ? <p className="bp-muted">{status}</p> : null}
          {error ? <p className="bp-error">{error}</p> : null}
        </div>
      </div>

      {cropSrc ? (
        <ImageCropModal
          src={cropSrc}
          title="Ajustar foto de perfil"
          onCancel={clearCropSrc}
          onConfirm={onCropConfirm}
        />
      ) : null}
    </section>
  )
}
